import { Component, inject, OnInit, signal, Type } from '@angular/core';
import { FoldersService } from '../../services/folders.service';
import { Folder } from '../../models/folder.model';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { catchError, EMPTY, Observable, tap } from 'rxjs';
import { FolderInventoryActionPricingComponentDetails } from './inventory-actions/pricing/folder-inventory-action-pricing-details.component';
import { NgComponentOutlet } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { HumanPipe } from '../../shared/pipes/human.pipe';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminService } from '../../admin/services/admin.service';
import { ConfirmDialogComponent } from '../../main/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-folder-details',
  templateUrl: './folder-details.component.html',
  styleUrl: './folder-details.component.scss',
  imports: [
    HumanPipe,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatToolbarModule,
    NgComponentOutlet,
  ]
})
export class FolderDetailsComponent implements OnInit {
  componentMap = {
    'Folder::InventoryAction::Pricing': FolderInventoryActionPricingComponentDetails,
  }

  public DetailsComponent = signal<Type<any> | null>(null);

  public loading = signal(false);

  public folder = signal<Folder | null>(null);


  protected readonly adminService = inject(AdminService);

  private readonly activatedRoute = inject(ActivatedRoute);

  public readonly data = inject(MAT_DIALOG_DATA);
  
  private readonly dialog = inject(MatDialog);

  public readonly dialogRef = inject(MatDialogRef);

  private readonly folderService = inject(FoldersService);

  private readonly snackBar = inject(MatSnackBar);


  get closed() {
    return this.folder().status === 'closed';
  }


  ngOnInit() {
    this.loading.set(true);

    if (this.data?.folder) {
      this.folder.set(this.data.folder);
      this.openDetails(this.folder().id).subscribe();
    } else {
      this.activatedRoute.params.subscribe((params) => {
        if (params.id) {
          this.openDetails(params.id).subscribe();;
        }
      });
    }
  }

  closeFolder() {
    const title = 'Cerrar ' + this.adminService.getText(this.folder().type);
    this.dialog.open(ConfirmDialogComponent, {
      data: { title: title, body: this.closeConfirmationMessage() },
    }).afterClosed().subscribe((result) => {
      if (!result) return;

      this.loading.set(true);
      this.folderService.close(this.folder().id)
        .pipe(
          catchError(() => {
            this.snackBar.open(
              'Error al cerrar ' + this.adminService.getText(this.folder().type),
              'Cerrar',
              { duration: 5000 }
            );
            return EMPTY;
          })
        )
        .subscribe((response: Folder) => {
          this.folder.set(response);
          this.loading.set(false);
        });
    });
  }

  reload() {
    this.openDetails(this.folder().id).subscribe();
  }

  private closeConfirmationMessage() {
    const mapMessageFolder = {
      'Folder::InventoryAction::Pricing': 'Esta acción rechazará todas las cotizaciones que no hayan sido aprobadas',
    };

    const defaultMessage = 'Esta acción cerrará la ' + this.adminService.getText(this.folder().type);
    const message = mapMessageFolder[this.folder().type] || defaultMessage;

    return `${message}. ¿Desea continuar?`;
  }

  private openDetails(id: number): Observable<Folder> {
    this.loading.set(true);
    return this.folderService.get(id).pipe(
      catchError(() => {
        return EMPTY;
      })
    ).pipe(
      tap((response) => {
        if (response) {
          this.folder.set(response);
          this.loadComponent();
        }
        this.loading.set(false);
      }),
    );
  }

  private loadComponent() {
    this.DetailsComponent.set(this.componentMap[this.folder().type] || null);
  }

}
